import {
  countCapMessage,
  EMPTY_FILE_MESSAGE,
  FILE_MAX_BYTES,
  formatBytes,
  overSizeMessage,
  type RecordFileItem,
} from '@ofmos/shared/files';
import { type ChangeEvent, useRef, useState } from 'react';
import { useAppClient } from '../../data/ClientProvider';
import { Button, Icon, Spinner, useIsDesktop, useToast } from '../../ui';
import { useFieldContext } from '../FieldContext';
import { FileList } from '../files/FileList';
import { fileItemsOf, maxFilesFor, remainingFor } from '../files/fileItems';

/**
 * THE LIVE ATTACHMENT LIST (`docs/decisions/file-attachments.md` D17, D18).
 *
 * THE PICKER IS THE ONE THIS REPOSITORY ALREADY HAS: a real `<input type="file">` behind a
 * Button, copied verbatim from `app/csv/UploadStage.tsx` with `multiple` added and `accept`
 * omitted - a CRM attaches whatever a customer sent. The reason that file gives holds here
 * too: the browser's own control cannot be restyled to the product's capsule, and a label
 * wrapping it would lose the pressed state every other control has.
 *
 * DRAG-AND-DROP IS NOT BUILT (D17), and not on taste. There is no HTML5 file drag-and-drop
 * anywhere in this bundle, it does not exist on a phone at all, and a drop zone needs a
 * drag-enter surface, a drop target, an active state, a leave-timeout and a wrong-type state
 * to not feel broken. The seam is this component: a future `DropZone` wraps it and calls the
 * same handler.
 *
 * THE CAPS ARE STATED BEFORE THEY BITE (section 12), the way `UploadStage.tsx:87-92` states
 * the CSV ones. The two client pre-checks - size and remaining count - save a round trip;
 * they do not make a decision, because the server refuses on the same two numbers with the
 * same two sentences.
 *
 * AN UPLOAD IS NEVER SILENTLY DROPPED AND NEVER BLOCKS THE FORM (D18). A file in flight is a
 * chip with an INDETERMINATE spinner and its byte count - not a percentage, because D5 keeps
 * every byte inside the one transport and `fetch` reports no upload progress, and a second
 * HTTP client is too high a price for a progress bar. A failed one is a chip carrying the
 * SERVER's sentence and a Retry.
 *
 * IT COMMITS ON COMPLETION. An upload has no later moment than the moment it finishes, so
 * there is nothing for a Save button to mean here - which is the same argument
 * `INSTANT_COMMIT_SHAPES` makes for the table cell.
 *
 * AND A COMMITTED FILE IS NOT A READABLE ONE UNTIL THE WRITE COMES BACK (wave 1c, finding 3).
 * The bytes are on the server after the PUT; what makes them readable is the LINK the record
 * write creates, and `/api/files/:id/content` correctly answers 404 for a file that has none
 * yet. Rendering `<img src=/api/files/:id/content>` from the optimistic value therefore asked
 * for a 404 on every upload and latched "This image could not be loaded." until the row
 * remounted - a sentence that is false about a file which is perfectly fine. So this
 * component tracks which of its own uploads are still UNCONFIRMED, and their previews wait.
 */

interface PendingUpload {
  readonly key: string;
  readonly file: File;
  readonly failure?: string | undefined;
}

export function FilesInput() {
  const { field, value, onChange, readOnly } = useFieldContext();
  const client = useAppClient();
  const toast = useToast();
  const isDesktop = useIsDesktop();
  const picker = useRef<HTMLInputElement>(null);
  /**
   * THE CONTROL A REMOVAL CAN HAND FOCUS BACK TO (wave 1c, finding M1).
   *
   * A Remove confirmation destroys its own opener - the button belongs to the row the
   * confirmation removes - so the close has to name something that survives. "Add files" is
   * the field's own stable control and sits directly under the list it adds to. It is passed
   * as a FALLBACK: a cancelled confirmation still returns focus to the row's Remove button,
   * because that one is still there.
   */
  const addFiles = useRef<HTMLButtonElement>(null);
  const [pending, setPending] = useState<readonly PendingUpload[]>([]);
  /**
   * THE FILES THIS COMPONENT HAS EMITTED AND NOT YET SEEN WRITTEN (wave 1c, finding 3).
   *
   * An id goes in when the upload finishes and comes out when the record write it triggers
   * SETTLES, whichever of the three outcomes it took - the field context's `onChange` returns
   * that promise on the two surfaces that write a record, and nothing at all on the fixture
   * provider, where "already settled" is the truth.
   */
  const [unconfirmed, setUnconfirmed] = useState<ReadonlySet<string>>(() => new Set());

  const items = fileItemsOf(value);
  /**
   * THE LIST THIS COMPONENT LAST KNEW ABOUT, which is not always the one the props carry.
   *
   * An upload's result is appended to the list at the moment it FINISHES, and the props do
   * not catch up until the write it triggers comes back. So the ref is adopted from the props
   * only when the props actually CHANGE, and is otherwise advanced by our own emissions -
   * without which a second upload would build its array from a value that predates the first
   * and silently drop it.
   */
  const latest = useRef<readonly RecordFileItem[]>(items);
  const seen = useRef(value);
  if (seen.current !== value) {
    seen.current = value;
    latest.current = items;
  }

  const maxFiles = maxFilesFor(field);
  const remaining = remainingFor(field, items.length + pending.length);

  const commit = (next: readonly RecordFileItem[]): Promise<void> | void => {
    latest.current = next;
    return onChange(next);
  };

  const send = async (entry: PendingUpload) => {
    try {
      const reserved = await client.files.reserve({
        label: entry.file.name,
        byteCount: entry.file.size,
      });
      const stored = await client.files.upload(reserved.fileId, entry.file);
      setPending((current) => current.filter((candidate) => candidate.key !== entry.key));
      setUnconfirmed((current) => new Set(current).add(stored.fileId));
      try {
        await commit([...latest.current, stored]);
      } finally {
        setUnconfirmed((current) => {
          const next = new Set(current);
          next.delete(stored.fileId);
          return next;
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'The upload did not finish.';
      setPending((current) =>
        current.map((candidate) =>
          candidate.key === entry.key ? { ...candidate, failure: message } : candidate,
        ),
      );
    }
  };

  /**
   * ONE FILE AT A TIME, AND THE QUEUE IS NOT A PERFORMANCE CHOICE.
   *
   * Each completed upload writes the RECORD (D18: an upload commits on completion), so two
   * finishing together would be two concurrent writes to one cell - and D7's link CAS would
   * refuse the second, because the transaction that has not seen the first still reads the
   * cell as empty and would try to link an already-linked id. The affected-count assertion
   * then throws and the whole record write rolls back, which is exactly the behaviour that
   * assertion exists to produce.
   *
   * Serialising is the honest fix rather than a workaround: at a 25 MB cap two files upload
   * one after the other in the time a person notices as "uploading", and every other answer
   * (batching the writes, retrying the loser, relaxing the CAS) trades a guarantee for a
   * second or two.
   */
  const queue = useRef<PendingUpload[]>([]);
  const draining = useRef(false);

  const drain = async () => {
    if (draining.current) return;
    draining.current = true;
    try {
      for (let next = queue.current.shift(); next !== undefined; next = queue.current.shift()) {
        await send(next);
      }
    } finally {
      draining.current = false;
    }
  };

  const enqueue = (entries: readonly PendingUpload[]) => {
    queue.current.push(...entries);
    void drain();
  };

  const pick = (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = [...(event.target.files ?? [])];
    // The same input is used again for the next file, so its value has to be cleared or
    // picking the SAME file twice fires no change event at all.
    event.target.value = '';
    if (chosen.length === 0) return;

    if (chosen.length > remaining) {
      toast({ tone: 'danger', title: countCapMessage(maxFiles) });
      return;
    }

    const accepted: PendingUpload[] = [];
    for (const file of chosen) {
      if (file.size === 0) {
        toast({ tone: 'danger', title: EMPTY_FILE_MESSAGE, description: file.name });
        continue;
      }
      if (file.size > FILE_MAX_BYTES) {
        toast({ tone: 'danger', title: overSizeMessage(file.size), description: file.name });
        continue;
      }
      accepted.push({ key: `${file.name}:${file.size}:${Date.now()}:${accepted.length}`, file });
    }
    if (accepted.length === 0) return;
    setPending((current) => [...current, ...accepted]);
    enqueue(accepted);
  };

  const remove = (fileId: string) => {
    commit(latest.current.filter((item) => item.fileId !== fileId));
  };

  return (
    <div className="space-y-2">
      {items.length === 0 && pending.length === 0 ? (
        <p className="text-sm text-ink-muted">
          No files yet. Up to {maxFiles} files, {formatBytes(FILE_MAX_BYTES)} each.
        </p>
      ) : (
        /**
         * A REAL LIST WITH A NAME. Two of these can be on one record page and the timeline
         * below repeats every filename in a sentence, so "the row for contract.pdf" is only
         * an unambiguous thing to point at if the list it lives in is addressable.
         */
        <FileList
          label={`${field.label} files`}
          items={items}
          unconfirmed={unconfirmed}
          returnFocusTo={addFiles}
          {...(readOnly ? {} : { onRemove: remove })}
        >
          {pending.map((entry) => (
            <li key={entry.key}>
              <UploadChip
                entry={entry}
                onRetry={() => {
                  setPending((current) =>
                    current.map((candidate) =>
                      candidate.key === entry.key
                        ? { key: candidate.key, file: candidate.file }
                        : candidate,
                    ),
                  );
                  enqueue([{ key: entry.key, file: entry.file }]);
                }}
                onDismiss={() =>
                  setPending((current) =>
                    current.filter((candidate) => candidate.key !== entry.key),
                  )
                }
              />
            </li>
          ))}
        </FileList>
      )}

      {!readOnly && (
        <div>
          {/*
            A real `<input type="file">` behind a Button, the pattern `UploadStage.tsx`
            established. `multiple` because a list field takes a list; no `accept`, because
            a CRM attaches whatever a customer sent and an accept filter would hide the
            file somebody actually has.
          */}
          <input
            ref={picker}
            type="file"
            multiple
            className="sr-only"
            onChange={pick}
            aria-label={`Add files to ${field.label}`}
          />
          <Button
            ref={addFiles}
            variant="secondary"
            icon="arrow-up"
            block={!isDesktop}
            disabled={remaining === 0}
            onClick={() => picker.current?.click()}
          >
            Add files
          </Button>
          {remaining === 0 && (
            <p className="mt-1 text-xs text-ink-muted">{countCapMessage(maxFiles)}</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A FILE IN FLIGHT, OR ONE THAT DID NOT MAKE IT (D18).
 *
 * The spinner is INDETERMINATE and the byte count is what stands in for progress, which is
 * the honest trade at a 25 MB cap: `fetch` reports no upload progress, and the alternative is
 * an `XMLHttpRequest` that bypasses the transport's cookie, error mapping, 401 hook and
 * fixture seam - four behaviours re-implemented for a percentage.
 *
 * A FAILURE KEEPS THE SERVER'S OWN SENTENCE. "HTML files are not accepted. Save the page as a
 * PDF and upload that." is a sentence somebody can act on; "Upload failed" is not.
 */
function UploadChip({
  entry,
  onRetry,
  onDismiss,
}: {
  readonly entry: PendingUpload;
  readonly onRetry: () => void;
  readonly onDismiss: () => void;
}) {
  const failed = entry.failure !== undefined;
  return (
    <div className="flex items-start gap-3 py-2">
      <span
        className={
          failed
            ? 'flex h-9 w-9 shrink-0 items-center justify-center rounded-control border border-line bg-danger-subtle text-danger'
            : 'flex h-9 w-9 shrink-0 items-center justify-center rounded-control border border-line bg-surface-sunken text-ink-muted'
        }
      >
        {failed ? <Icon name="warning" size="1.1rem" /> : <Spinner />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-ink">{entry.file.name}</p>
        {failed ? (
          <>
            <p className="text-xs text-danger">{entry.failure}</p>
            <div className="mt-1 flex flex-wrap items-center gap-1">
              <Button variant="ghost" size="sm" onClick={onRetry}>
                Retry
              </Button>
              <Button variant="ghost" size="sm" onClick={onDismiss}>
                Dismiss
              </Button>
            </div>
          </>
        ) : (
          <p className="text-xs text-ink-muted tabular-nums">
            Uploading, {formatBytes(entry.file.size)}
          </p>
        )}
      </div>
    </div>
  );
}
